/**
 * rc143 — Reglas del motor de facturación/cobranza (antes hardcodeadas) +
 * tarjeta "Cómo decide el sistema": línea de tiempo calculada con los valores
 * vigentes y simulación del ciclo de cobranza de hoy (dry-run).
 *
 * Vive dentro de Ajustes › Facturación (ConfigFacturacionSection) con su
 * propio botón de guardar para no acoplarse al formulario grande.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, SlidersHorizontal, Route, Play, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useConfigByGrupo, useUpdateConfig, type ConfigItem } from "@/features/config/api";
import { apiCall, type ApiError } from "@/lib/api";

const K = {
  modelo: "facturacion.modelo_periodo",
  instDias: "facturacion.instalacion_dias_vencimiento",
  factGap: "facturacion.recovery_gap_horas",
  alcance: "cobranza.alcance_suspension",
  libres: "cobranza.cargos_libres_cuentan",
  recSusp: "cobranza.recovery_suspende",
  cobGap: "cobranza.recovery_gap_horas",
  defMax: "contratos.default_max_facturas_vencidas",
  defFactSusp: "contratos.default_facturar_suspendido",
  // solo lectura aquí (se editan arriba) — para la línea de tiempo
  anticipacion: "facturacion.dias_anticipacion",
  horaFact: "facturacion.hora_ejecucion",
  gracia: "cobranza.dias_gracia",
  horaCorte: "cobranza.hora_corte",
} as const;

function val(items: ConfigItem[] | undefined, key: string, def: unknown): unknown {
  const it = items?.find((i) => i.key === key);
  return it === undefined ? def : it.value;
}
function asBool(v: unknown): boolean {
  return v === true || v === "true" || v === 1 || v === "1";
}

type CobranzaPreview = {
  contratos_evaluados: number;
  facturas_migradas_a_vencidas: number;
  detalle_suspendidos: { codigo: string; cliente_id: string }[];
};

export function ReglasMotorCard() {
  const { data: fact } = useConfigByGrupo("facturacion");
  const { data: cob } = useConfigByGrupo("cobranza");
  const updateMut = useUpdateConfig();

  const [modelo, setModelo] = useState("mes_vencimiento");
  const [instDias, setInstDias] = useState("0");
  const [factGap, setFactGap] = useState("25");
  const [alcance, setAlcance] = useState("contrato");
  const [libres, setLibres] = useState(false);
  const [recSusp, setRecSusp] = useState(true);
  const [cobGap, setCobGap] = useState("25");
  const [defMax, setDefMax] = useState("0");
  const [defFactSusp, setDefFactSusp] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState<CobranzaPreview | null>(null);
  const [simulando, setSimulando] = useState(false);

  useEffect(() => {
    if (!fact) return;
    setModelo(String(val(fact, K.modelo, "mes_vencimiento")));
    setInstDias(String(val(fact, K.instDias, 0)));
    setFactGap(String(val(fact, K.factGap, 25)));
  }, [fact]);
  useEffect(() => {
    if (!cob) return;
    setAlcance(String(val(cob, K.alcance, "contrato")));
    setLibres(asBool(val(cob, K.libres, false)));
    setRecSusp(asBool(val(cob, K.recSusp, true)));
    setCobGap(String(val(cob, K.cobGap, 25)));
    setDefMax(String(val(cob, K.defMax, 0)));
    setDefFactSusp(asBool(val(cob, K.defFactSusp, false)));
  }, [cob]);
  useEffect(() => { if (fact && cob) setDirty(false); }, [fact, cob]);

  // ── Línea de tiempo con los valores vigentes (los editables arriba) ──
  const timeline = useMemo(() => {
    const ant = Number(val(fact, K.anticipacion, 5));
    const gr = Number(val(cob, K.gracia, 5));
    const hf = String(val(fact, K.horaFact, "09:00"));
    const hc = String(val(cob, K.horaCorte, "08:00"));
    // Ejemplo con corte día 1 sobre el próximo mes real (así las fechas cuadran)
    const corte = 1;
    const hoy = new Date();
    const venc = new Date(hoy.getFullYear(), hoy.getMonth() + 1, corte);
    const emi = new Date(venc); emi.setDate(emi.getDate() - ant);
    const susp = new Date(venc); susp.setDate(susp.getDate() + gr + 1);
    const f = (d: Date) => d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
    return { ant, gr, hf, hc, corte, emisionTxt: f(emi), vencTxt: f(venc), vencidaTxt: f(new Date(venc.getFullYear(), venc.getMonth(), venc.getDate() + 1)), suspTxt: f(susp) };
  }, [fact, cob]);

  async function guardar() {
    const n = (s: string, min: number, max: number, nombre: string) => {
      const x = Number(s);
      if (!Number.isInteger(x) || x < min || x > max) throw new Error(`${nombre}: entero entre ${min} y ${max}`);
      return x;
    };
    try {
      const vInst = n(instDias, 0, 60, "Plazo de instalación");
      const vFactGap = n(factGap, 2, 168, "Horas de recovery (facturación)");
      const vCobGap = n(cobGap, 2, 168, "Horas de recovery (cobranza)");
      const vDefMax = n(defMax, 0, 12, "Tolerancia default");
      await Promise.all([
        updateMut.mutateAsync({ key: K.modelo, value: modelo, value_type: "string", grupo: "facturacion" }),
        updateMut.mutateAsync({ key: K.instDias, value: vInst, value_type: "int", grupo: "facturacion" }),
        updateMut.mutateAsync({ key: K.factGap, value: vFactGap, value_type: "int", grupo: "facturacion" }),
        updateMut.mutateAsync({ key: K.alcance, value: alcance, value_type: "string", grupo: "cobranza" }),
        updateMut.mutateAsync({ key: K.libres, value: libres, value_type: "bool", grupo: "cobranza" }),
        updateMut.mutateAsync({ key: K.recSusp, value: recSusp, value_type: "bool", grupo: "cobranza" }),
        updateMut.mutateAsync({ key: K.cobGap, value: vCobGap, value_type: "int", grupo: "cobranza" }),
        updateMut.mutateAsync({ key: K.defMax, value: vDefMax, value_type: "int", grupo: "cobranza" }),
        updateMut.mutateAsync({ key: K.defFactSusp, value: defFactSusp, value_type: "bool", grupo: "cobranza" }),
      ]);
      toast.success("Reglas del motor guardadas", { description: "Aplican desde el próximo ciclo (y en las simulaciones de inmediato)." });
      setDirty(false);
    } catch (e) {
      toast.error("No se guardó", { description: (e as ApiError).message ?? String(e) });
    }
  }

  async function simularCobranza() {
    setSimulando(true);
    try {
      const r = await apiCall<CobranzaPreview>("get", "/api/v1/cobranza/preview");
      setPreview(r);
      toast.info(r.detalle_suspendidos.length === 0 ? "Hoy nadie se suspendería" : `${r.detalle_suspendidos.length} contrato(s) se suspenderían hoy`);
    } catch (e) {
      toast.error("No se pudo simular", { description: (e as ApiError).message });
    } finally { setSimulando(false); }
  }

  const mark = () => setDirty(true);

  return (
    <>
      {/* ── Reglas del motor ── */}
      <div className="lg:col-span-2 rounded-lg border bg-card p-4 space-y-4">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} className="text-primary" />
          <Label className="text-sm font-semibold">Reglas del motor (antes fijas en el código)</Label>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Periodo de la mensualidad</label>
            <Select value={modelo} onChange={(e) => { setModelo(e.target.value); mark(); }} className="w-full">
              <option value="mes_vencimiento">Mes natural del vencimiento (1 al último día)</option>
              <option value="corte_a_corte">Del corte al día anterior del siguiente corte (prepago)</option>
              <option value="pospago_corte">Del corte anterior al día anterior del vencimiento (pospago)</option>
            </Select>
            <p className="text-[11px] text-muted-foreground">Con corte día 1 los tres dan lo mismo. Con corte día 15, "mes natural" imprime "01-oct a 31-oct" en una factura que vence el 15-oct; los otros dos imprimen el ciclo real.</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Alcance de la suspensión</label>
            <Select value={alcance} onChange={(e) => { setAlcance(e.target.value); mark(); }} className="w-full">
              <option value="contrato">Por contrato — cada servicio responde por sus mensualidades</option>
              <option value="cliente">Por cliente — una vencida suspende todos sus servicios</option>
            </Select>
            <p className="text-[11px] text-muted-foreground">La tolerancia (facturas vencidas permitidas) se compara contra ese conteo. Las facturas del cliente sin contrato cuentan en ambos.</p>
          </div>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={libres} onChange={(e) => { setLibres(e.target.checked); mark(); }} className="mt-0.5" />
            <span><b>Los cargos libres también cortan</b><br /><span className="text-muted-foreground">Servicio adicional, instalación, etc. Por default solo las mensualidades suspenden.</span></span>
          </label>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Plazo de la factura de instalación</label>
            <div className="flex items-center gap-2">
              <Input type="number" min={0} max={60} value={instDias} onChange={(e) => { setInstDias(e.target.value); mark(); }} className="w-24 font-mono" />
              <span className="text-xs text-muted-foreground">días (0 = vence el día del alta)</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Contratos nuevos: tolerancia por default</label>
            <div className="flex items-center gap-2">
              <Input type="number" min={0} max={12} value={defMax} onChange={(e) => { setDefMax(e.target.value); mark(); }} className="w-24 font-mono" />
              <span className="text-xs text-muted-foreground">mensualidades vencidas permitidas antes de suspender</span>
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={defFactSusp} onChange={(e) => { setDefFactSusp(e.target.checked); mark(); }} className="mt-0.5" />
            <span><b>Contratos nuevos: seguir facturando suspendido</b><br /><span className="text-muted-foreground">Si no, al suspenderse deja de emitirse mensualidad y al reactivarse entra a "ciclo por revisar".</span></span>
          </label>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Recuperación tras caída (horas)</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-20">Facturación</span>
              <Input type="number" min={2} max={168} value={factGap} onChange={(e) => { setFactGap(e.target.value); mark(); }} className="w-24 font-mono" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-20">Cobranza</span>
              <Input type="number" min={2} max={168} value={cobGap} onChange={(e) => { setCobGap(e.target.value); mark(); }} className="w-24 font-mono" />
            </div>
            <p className="text-[11px] text-muted-foreground">Si un poller estuvo apagado más de estas horas, al arrancar ejecuta el ciclo perdido.</p>
          </div>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={recSusp} onChange={(e) => { setRecSusp(e.target.checked); mark(); }} className="mt-0.5" />
            <span><b>La recuperación de cobranza sí suspende</b><br /><span className="text-muted-foreground">Desmarcado: al recuperar un ciclo perdido solo marca vencidas y las suspensiones esperan a la hora de corte (evita cortar a media tarde tras una caída).</span></span>
          </label>
        </div>

        <div className="flex items-center justify-end">
          <Button size="sm" onClick={guardar} disabled={!dirty || updateMut.isPending}>
            {updateMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar reglas
          </Button>
        </div>
      </div>

      {/* ── Cómo decide el sistema ── */}
      <div className="lg:col-span-2 rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Route size={14} className="text-primary" />
          <Label className="text-sm font-semibold">Cómo decide el sistema (con tus valores actuales)</Label>
        </div>
        <ol className="space-y-1.5 text-xs">
          <li className="flex gap-2"><span className="font-mono text-muted-foreground w-28 shrink-0">{timeline.emisionTxt} · {timeline.hf}</span><span>Se emite la mensualidad: faltan <b>{timeline.ant}</b> días para el corte (ejemplo con corte día {timeline.corte}). Periodo según "{modelo === "mes_vencimiento" ? "mes natural" : modelo === "corte_a_corte" ? "corte a corte" : "pospago"}".</span></li>
          <li className="flex gap-2"><span className="font-mono text-muted-foreground w-28 shrink-0">{timeline.vencTxt}</span><span>Vence la factura (corte día {timeline.corte}).</span></li>
          <li className="flex gap-2"><span className="font-mono text-muted-foreground w-28 shrink-0">{timeline.vencidaTxt} · {timeline.hc}</span><span>Pasa a estatus "vencida" (el cliente la ve así en el portal).</span></li>
          <li className="flex gap-2"><span className="font-mono text-muted-foreground w-28 shrink-0">{timeline.suspTxt} · {timeline.hc}</span><span>Se suspende si las vencidas <b>{alcance === "cliente" ? "del cliente" : "del contrato"}</b> superan su tolerancia y no hay promesa de pago vigente (gracia {timeline.gr} → corte al día siguiente de agotarse).</span></li>
          <li className="flex gap-2"><span className="font-mono text-muted-foreground w-28 shrink-0">al pagar</span><span>Reactivación inmediata (job al router) si el contrato queda dentro de su tolerancia. También al anular la vencida, al aplicar saldo a favor o al otorgar una promesa.</span></li>
        </ol>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={simularCobranza} disabled={simulando}>
            {simulando ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Simular cobranza de hoy
          </Button>
          <span className="text-[11px] text-muted-foreground">No suspende nada; muestra qué haría el ciclo de las {timeline.hc}.</span>
        </div>
        {preview && (
          <div className="rounded border bg-muted/30 p-3 text-xs space-y-1">
            <div className="flex items-center gap-2"><ShieldAlert size={13} className={preview.detalle_suspendidos.length ? "text-destructive" : "text-emerald-500"} />
              <b>{preview.detalle_suspendidos.length}</b> contrato(s) se suspenderían · {preview.facturas_migradas_a_vencidas} factura(s) pasarían a vencida</div>
            {preview.detalle_suspendidos.length > 0 && (
              <div className="font-mono text-[11px] text-muted-foreground">{preview.detalle_suspendidos.slice(0, 30).map((d) => d.codigo).join(", ")}{preview.detalle_suspendidos.length > 30 ? ` +${preview.detalle_suspendidos.length - 30} más` : ""}</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
